import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";

import { taskboard } from "../../services/taskboardService";
import { ENTRY_MODES, STATUS_COLUMNS, entryBoardStatus } from "../../constants/taskboardStatus";
import ClubPicker from "./taskboard/ClubPicker";

// ──────────────────────────────────────────────────────────────────────
// AddEntryModal — create OR edit a deal-tracker entry (product rule 3:
// entries are fully editable via the same modal, prefilled). When an
// `entry` is passed it edits (PATCH) and prefills; otherwise it creates.
//
// Status uses the new set (Open / Waiting / In Progress / Done). Club is
// chosen from the real ClubPicker (logo + name + country, "No club"
// valid, unlisted name-only fallback) — free text as default is gone.
// Editing an entry NEVER touches its update thread (only the fields
// here change); the backend preserves the thread.
// ──────────────────────────────────────────────────────────────────────

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const AddEntryModal = ({ isOpen, onClose, folderId, entry = null, defaultStatus = "open", onSuccess }) => {
  const isEdit = !!entry;
  const [mode, setMode] = useState("phone");
  const [date, setDate] = useState(todayISO());
  const [club, setClub] = useState(null); // { wyscout_team_id, name, country, logo } | { name, unlisted:true } | null
  const [notes, setNotes] = useState("");
  const [statusValue, setStatusValue] = useState(defaultStatus);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (entry) {
      setMode(entry.mode_of_communication || "phone");
      setDate((entry.date || todayISO()).slice(0, 10));
      setNotes(entry.notes || "");
      setStatusValue(entryBoardStatus(entry));
      const snap = entry.club_snapshot || null;
      const clubName = snap?.name || entry.club_name_snapshot || entry.club || "";
      const teamId = snap?.wyscout_team_id || entry.wyscout_team_id || null;
      const clubLogo = snap?.logo || entry.club_logo_snapshot || null;
      if (clubName) {
        setClub(
          teamId || clubLogo
            ? { wyscout_team_id: teamId, name: clubName, country: entry.country, logo: clubLogo }
            : { name: clubName, unlisted: true },
        );
      } else {
        setClub(null);
      }
    } else {
      setMode("phone");
      setDate(todayISO());
      setClub(null);
      setNotes("");
      setStatusValue(defaultStatus || "open");
    }
  }, [isOpen, entry, defaultStatus]);

  // Club input per the backend contract: EITHER picker fields
  // {wyscout_team_id, club_name_snapshot, club_logo_snapshot}, OR an
  // unlisted {club:"typed"}, OR clears for "No club".
  const buildClubPayload = () => {
    if (!club) return { wyscout_team_id: null, club: "" };
    if (club.unlisted) return { wyscout_team_id: null, club: club.name };
    return {
      wyscout_team_id: club.wyscout_team_id,
      club_name_snapshot: club.name,
      club_logo_snapshot: club.logo || null,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!notes.trim()) {
      toast.error("Notes are required");
      return;
    }
    setSubmitting(true);
    const payload = {
      mode_of_communication: mode,
      date,
      notes: notes.trim(),
      status: statusValue,
      ...buildClubPayload(),
    };
    const response = isEdit
      ? await taskboard.patchEntry(folderId, entry.id, payload)
      : await taskboard.createEntry(folderId, payload);
    setSubmitting(false);
    if (!response.success) {
      toast.error(response.error || (isEdit ? "Failed to save entry" : "Failed to add entry"));
      return;
    }
    toast.success(isEdit ? "Entry saved" : "Entry added");
    onSuccess && onSuccess(response.data);
    onClose && onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full overflow-visible" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
          <h2 className="text-xl font-semibold text-[#0B0B0B]">{isEdit ? "Edit entry" : "Add entry"}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#0B0B0B] mb-1">Type</label>
              <select value={mode} onChange={(e) => setMode(e.target.value)} className="w-full px-3 py-2 border border-[#E9E9E9] rounded-lg text-sm bg-white">
                {ENTRY_MODES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0B0B0B] mb-1">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-3 py-2 border border-[#E9E9E9] rounded-lg text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B0B0B] mb-1">Club</label>
            <ClubPicker value={club} onChange={setClub} />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B0B0B] mb-1">Notes *</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} required placeholder="What was discussed?" className="w-full px-3 py-2 border border-[#E9E9E9] rounded-lg text-sm resize-none" />
            {isEdit && <p className="text-[11px] text-[#98A2B3] mt-1">Editing the note here does not touch the update thread.</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B0B0B] mb-1">Status</label>
            <select value={statusValue} onChange={(e) => setStatusValue(e.target.value)} className="w-full px-3 py-2 border border-[#E9E9E9] rounded-lg text-sm bg-white">
              {STATUS_COLUMNS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2 border border-[#E9E9E9] rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={submitting} className="px-4 py-2 bg-[#2283FF] text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? "Saving…" : isEdit ? "Save entry" : "Add entry"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default AddEntryModal;
